import { useEffect, useRef, useState } from 'react';
import Honeybadger from 'honeybadger-js';
import { ThemesQuery } from '../../generated/graphql';

export enum SortOrder {
  pinned = 'pinned',
}

interface State {
  themes: ThemesQuery['familyThemes'];
  loading: boolean;
  sortOrder: SortOrder;
}

export default function useThemes(): State {
  const [state, setState] = useState<State>({
    themes: [],
    loading: true,
    sortOrder: SortOrder.pinned,
  });
  const cancelRequest = useRef<boolean>(false);

  const randomizeThemes = (themes: ThemesQuery['familyThemes']) => {
    const now = new Date();
    const day = now.getDate();
    const hour = now.getHours();
    const seed = day * 24 + hour; // Unique seed for each hour of each day
    let random = seed;
    const seededRandom = () => {
      random = (random * 9301 + 49297) % 233280;
      return random / 233280;
    };

    // Fisher-Yates shuffle with seeded random
    const shuffled = [...themes];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    cancelRequest.current = false;
    const themeUrl = process.env.REACT_APP_STATIC_PINNED_THEME_URL!;

    const fetchData = async () => {
      try {
        const response = await fetch(themeUrl);
        const data = await response.json();

        if (cancelRequest.current) return;

        const themes: ThemesQuery['familyThemes'] = data?.data?.familyThemes || [];
        //take the first 6 themes as pinned themes
        const themesCopy = [...themes];
        const orderedThemes = themesCopy.splice(0, 6);
        const randomizedThemes = randomizeThemes(themesCopy);

        setState(prevState => ({
          ...prevState,
          loading: false,
          themes: [...orderedThemes, ...randomizedThemes],
          sortOrder: SortOrder.pinned,
        }));
      } catch (error) {
        if (cancelRequest.current) return;

        setState(prevState => ({
          ...prevState,
          loading: false,
          sortOrder: SortOrder.pinned,
        }));

        Honeybadger.notify(error as Error, {
          name: 'StaticThemesError',
        });
      }
    };

    void fetchData();

    return () => {
      cancelRequest.current = true;
    };
  }, []);

  return state;
}
